import { faBox, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { DesktopDatePicker } from '@mui/lab';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from 'common/card/Card';
import CardBody from 'common/card/CardBody';
import CardHead from 'common/card/CardHead';
import { InputLabelProps } from 'common/DatePicker';
import { NotificationContext } from './Notification';
import { AuthenticationContext } from './providers/AuthCtx';
import { PartOrder } from 'data/part_order';
import { PartOrderForm, defaultPartOrderForm, partOrderToForm } from 'data/part_order_form';
import { PartOrderType, PartOrderTypeLabels, PartOrderLang, PartOrderLangLabels } from 'data/part_order';
import { createPartOrder, updatePartOrder, getPartOrder } from 'services/part_order_service';
import { handleError } from 'services/api';
import { ApiError } from 'data/api_error';
import { Message } from 'data/message';
import { useEdit, validateRequired } from 'hooks/edit_hook';

interface DatePickerWithClearProps {
    label: string;
    value: moment.Moment | undefined;
    onChange: (value: moment.Moment | null) => void;
    error?: boolean;
    helperText?: string;
}

const DatePickerWithClear: React.FC<DatePickerWithClearProps> = ({ label, value, onChange, error, helperText }) => {
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', marginBottom: '1rem', width: "100%" }}>
            <DesktopDatePicker
                label={label}
                value={value ?? null}
                onChange={onChange}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="standard"
                        InputLabelProps={InputLabelProps}
                        error={error}
                        helperText={helperText}
                        fullWidth
                    />
                )}
            />
            {value && (
                <Button
                    size="small"
                    variant="contained"
                    onClick={() => onChange(null)}
                    startIcon={<FontAwesomeIcon icon={faTimes} />}
                    style={{ marginBottom: '0.5rem', minWidth: '0px' }}
                >
                </Button>
            )}
        </div>
    );
};

interface ReadOnlyFieldProps {
    label: string;
    value: string | undefined;
}

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value }) => {
    return (
        <div style={{ marginBottom: '1rem', width: "100%" }}>
            <label style={{ color: 'var(--color-secondary)', fontSize: '0.75rem', display: 'block', marginBottom: '0.25rem' }}>
                {label}
            </label>
            <div style={{ padding: '0.5rem 0', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', color: 'var(--color-secondary)' }}>
                {value || '-'}
            </div>
        </div>
    );
};

const PartOrderFormPage: React.FC = () => {
    const authCtx = useContext(AuthenticationContext);
    const notificationHandler = useContext(NotificationContext);
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [form, handleChange, dispatch] = useEdit<PartOrderForm>(defaultPartOrderForm());

    const fetchPartOrder = useCallback(() => {
        if (!id) return;
        setLoading(true);
        getPartOrder(authCtx, id).then((data: PartOrder) => {
            dispatch(partOrderToForm(data));
        }).catch((e: ApiError) => {
            handleError(e, notificationHandler);
            navigate('/part-orders');
        }).finally(() => {
            setLoading(false);
        });
    }, [authCtx, id, notificationHandler, navigate, dispatch]);

    useEffect(() => {
        if (isEditMode) {
            fetchPartOrder();
        }
    }, [isEditMode, fetchPartOrder]);

    const validateForm = useCallback((): boolean => {
        const newErrors: Record<string, string> = {};

        const orderNumError = validateRequired(form, 'order_num');
        if (orderNumError) newErrors.order_num = orderNumError;

        const phoneError = validateRequired(form, 'phone_no');
        if (phoneError) newErrors.phone_no = phoneError;

        const sellDateError = validateRequired(form, 'sell_date');
        if (sellDateError) newErrors.sell_date = sellDateError;

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [form]);

    const handleSubmit = useCallback(() => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        const action = isEditMode
            ? updatePartOrder(authCtx, form)
            : createPartOrder(authCtx, form);

        action.then((res: Message) => {
            notificationHandler({
                type: "success",
                message: res.message,
                timeout: 4000,
            });
            navigate('/part-orders');
        }).catch((e: ApiError) => {
            handleError(e, notificationHandler);
        }).finally(() => {
            setLoading(false);
        });
    }, [authCtx, form, isEditMode, navigate, notificationHandler, validateForm]);

    if (isEditMode && !form.id) {
        return <div>Chargement...</div>;
    }

    return (
        <div
            className="container"
            style={{
                gridTemplateColumns: "15% 70% 15%",
                marginTop: "5%",
            }}
        >
            <div style={{ gridArea: "card" }}>
                <Card>
                    <CardHead>
                        <FontAwesomeIcon icon={faBox}></FontAwesomeIcon>
                    </CardHead>
                    <CardBody>
                        <h2>{isEditMode ? 'Modifier la commande' : 'Nouvelle commande'}</h2>

                        <TextField
                            InputLabelProps={InputLabelProps}
                            label="N° de commande"
                            variant="standard"
                            value={form.order_num}
                            error={!!errors.order_num}
                            helperText={errors.order_num}
                            onChange={(e) => handleChange('order_num', e.target.value)}
                            fullWidth
                            style={{ marginBottom: '1rem' }}
                        />

                        <TextField
                            InputLabelProps={InputLabelProps}
                            label="Numéro de téléphone"
                            variant="standard"
                            value={form.phone_no}
                            error={!!errors.phone_no}
                            helperText={errors.phone_no}
                            onChange={(e) => handleChange('phone_no', e.target.value)}
                            fullWidth
                            style={{ marginBottom: '1rem' }}
                        />

                        <FormControl variant="standard" fullWidth style={{ marginBottom: '1rem' }}>
                            <InputLabel style={{ color: 'var(--color-secondary)' }}>Type</InputLabel>
                            <Select
                                value={form.type}
                                onChange={(e) => dispatch({
                                    type: e.target.value as PartOrderType,
                                    part_lifetime_months: e.target.value === 'F' ? 6 : undefined,
                                })}
                                label="Type"
                            >
                                <MenuItem value="P">{PartOrderTypeLabels.P}</MenuItem>
                                <MenuItem value="F">{PartOrderTypeLabels.F}</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl variant="standard" fullWidth style={{ marginBottom: '1rem' }}>
                            <InputLabel style={{ color: 'var(--color-secondary)' }}>Langue</InputLabel>
                            <Select
                                value={form.lang}
                                onChange={(e) => handleChange('lang', e.target.value as PartOrderLang)}
                                label="Langue"
                            >
                                <MenuItem value="fr">{PartOrderLangLabels.fr}</MenuItem>
                                <MenuItem value="en">{PartOrderLangLabels.en}</MenuItem>
                            </Select>
                        </FormControl>

                        <DatePickerWithClear
                            label="Date de vente"
                            value={form.sell_date ? moment.utc(form.sell_date) : undefined}
                            onChange={(value) => handleChange('sell_date', value || undefined)}
                            error={!!errors.sell_date}
                            helperText={errors.sell_date}
                        />

                        <DatePickerWithClear
                            label="Date de réception"
                            value={form.date_received ? moment.utc(form.date_received) : undefined}
                            onChange={(value) => handleChange('date_received', value || undefined)}
                        />

                        <TextField
                            InputLabelProps={InputLabelProps}
                            label="Intervalle d'appel (heures)"
                            variant="standard"
                            type="number"
                            value={form.call_try_interval_hours}
                            onChange={(e) => handleChange('call_try_interval_hours', parseInt(e.target.value) || 0)}
                            fullWidth
                            style={{ marginBottom: '1rem' }}
                        />

                        {form.type === 'F' && (
                            <TextField
                                InputLabelProps={InputLabelProps}
                                label="Durée de vie de la pièce (mois)"
                                variant="standard"
                                type="number"
                                value={form.part_lifetime_months || ''}
                                onChange={(e) => handleChange('part_lifetime_months', e.target.value ? parseInt(e.target.value) : undefined)}
                                fullWidth
                                style={{ marginBottom: '1rem' }}
                            />
                        )}

                        {form.id &&
                            <>
                                <ReadOnlyField
                                    label="Date de dernier appel"
                                    value={form.last_call_date ? moment.utc(form.last_call_date)?.local().format('YYYY-MM-DD HH:mm') : '-'}
                                />

                                <ReadOnlyField
                                    label="Confirmé"
                                    value={form.confirmed ? 'Oui' : 'Non'}
                                />

                                <ReadOnlyField
                                    label="Date de confirmation"
                                    value={form.confirmed_at_date ? moment.utc(form.confirmed_at_date)?.local().format('YYYY-MM-DD HH:mm') : '-'}
                                />
                            </>
                        }

                        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FontAwesomeIcon icon={faSave} />}
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                Enregistrer
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => navigate('/part-orders')}
                                disabled={loading}
                            >
                                Annuler
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default PartOrderFormPage;
